import { FC, useEffect, useMemo, useRef, useState } from "react";

import { Stack } from "@mui/material";
import {
  CampaignPaymentTab,
  Locales,
  MembershipValidityPeriod,
  RouteBuilders,
  TicketingFormCategory,
  isWebBasedTicketsEligible,
  offersPickup,
  offersShipping,
  supportsFulfillment,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";

import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { AuctionRows } from "./categories/AuctionRows/AuctionRows";
import { EventRows } from "./categories/EventRows/EventRows";
import { MembershipRows } from "./categories/MembershipRows/MembershipRows";
import { ShopRows } from "./categories/ShopRows/ShopRows";
import { SimpleRows } from "./categories/SimpleRows/SimpleRows";
import { CustomMessagesDisabledBanner } from "./components/CustomMessagesDisabledBanner";
import { buildDefaultValues } from "./helpers/buildDefaultValues";
import { useShouldShowCustomMessagesDisabledBanner } from "./helpers/useShouldShowCustomMessagesDisabledBanner";
import { useSmsOptInConfirmation } from "./helpers/useSmsOptInConfirmation";
import { useApplyReminderAIDefaults } from "./hooks/useApplyReminderAIDefaults";
import { useApplyThankYouAIDefaults } from "./hooks/useApplyThankYouAIDefaults";
import { SmsOptInConfirmModal } from "./SmsOptInConfirmModal/SmsOptInConfirmModal";
import { AUTOMATED_EMAIL_OPENED_CARD_VALUES, AutomatedCommunicationsFormData, RowKey } from "./types";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";
import { useWarnIfUnsavedChanges } from "@/common/hooks/useWarnIfUnsavedChanges";
import { StepContainer } from "@/components/LiveEditorElements/StepContainer/StepContainer";
import { EmailContentsLoadState } from "@/features/LiveFormEditor/common/EmailContentsLoadState";
import { FormEmailContents } from "@/features/LiveFormEditor/common/formEmailContents";
import { LiveFormActionFooterPortal } from "@/features/LiveFormEditor/common/LiveFormEditorActionFooter";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";

const getInitialOpenCardFromUrl = (searchParams: ReturnType<typeof useSearchParams>): RowKey | null => {
  const openedCard = searchParams.get("openedCard");
  return AUTOMATED_EMAIL_OPENED_CARD_VALUES.includes(openedCard as RowKey) ? (openedCard as RowKey) : null;
};

export const resolveReminderFieldValue = ({
  dirty,
  displayedValue,
  storedValue,
}: {
  dirty: boolean | undefined;
  displayedValue: string;
  storedValue: string | null | undefined;
}): string => (!dirty && storedValue ? storedValue : displayedValue || "");

export const resolveReminderAttachmentPayload = ({
  dirty,
  displayedFileUrl,
  isStoredFileKnown,
}: {
  dirty: boolean;
  displayedFileUrl: string | null | undefined;
  isStoredFileKnown: boolean;
}): { attachmentUrl?: string | null } =>
  dirty || isStoredFileKnown ? { attachmentUrl: displayedFileUrl ?? null } : {};

const FORM_KEY_TO_ROW: Record<keyof AutomatedCommunicationsFormData, RowKey> = {
  thankYouWinner: "thank-you-email",
  thankYouBidPlaced: "thank-you-bid-placed",
  thankYouNonWinner: "thank-you-non-winner",
  outbid: "outbid",
  reminder1: "reminder-1",
  reminder2: "reminder-2",
  expiringMembership: "expiring-membership",
  autoRenewal: "auto-renewal",
  readyForPickup: "ready-for-pickup",
  orderShipped: "order-shipped",
};

const FORM_KEY_ORDER = Object.keys(FORM_KEY_TO_ROW) as (keyof AutomatedCommunicationsFormData)[];

export const TicketingLiveEditorStepAutomatedCommunications: FC = () => {
  const { t } = useTranslate();
  const { isStepChanging, apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();
  const emailContentsQuery = trpc.getAutomatedEmailContents.useQuery({ ticketingId: apiTicketing.id });

  if (emailContentsQuery.data) {
    return <TicketingAutomatedCommunicationsForm emailContents={emailContentsQuery.data} />;
  }

  return (
    <StepContainer
      title={t("dashboard", "ticketing.automatedCommunications.title")}
      description={t("dashboard", "ticketing.automatedCommunications.description")}
      isStepChanging={isStepChanging}
      slots={{ actionFooter: null }}
      data-test="live-form-editor-step-automated-communications"
    >
      <EmailContentsLoadState hasError={emailContentsQuery.isError} onRetry={() => void emailContentsQuery.refetch()} />
    </StepContainer>
  );
};

const TicketingAutomatedCommunicationsForm: FC<{ emailContents: FormEmailContents }> = ({ emailContents }) => {
  const { t } = useTranslate();
  const searchParams = useSearchParams();
  const {
    isStepChanging,
    apiFormObject: apiTicketing,
    formCategory,
    remindersData: remindersDataFromContext,
    showRenewalEmails,
    liveFormData,
    setLiveFormData,
  } = useLiveFormEditorContext<"ticketing">();
  const { currentUser, organization } = useCurrentUserContext();
  const { isoLocale } = useLocaleContext();
  const router = useRouter();
  const buildUrl = useUrlBuilder();

  const isMultiDate = (liveFormData?.occurrencesWithRates?.length ?? 0) > 1;

  const onViewReminderResults = ({ emailDeliveryId }: { emailDeliveryId: string | null }) => {
    if (isMultiDate || !emailDeliveryId) {
      const hubRoute = RouteBuilders.buildCampaignHubTabLink({
        isoLocale,
        formId: apiTicketing.id,
        tab: CampaignPaymentTab.Communication,
      });
      void router.push(buildUrl({ route: hubRoute, includeRouterSearchParams: false }));
      return;
    }
    const editRoute = RouteBuilders.buildTicketingFormEditLink({ isoLocale, formId: apiTicketing.id });
    const returnUrl = buildUrl({ route: editRoute, hash: "automated-communications" });
    const emailDetailUrl = RouteBuilders.buildEmailDeliveryPageLink({ emailDeliveryId }).path;
    void router.push(`${emailDetailUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
  };

  const isAuction = formCategory === TicketingFormCategory.Auction;
  const isEvent = formCategory === TicketingFormCategory.Event;
  const isMembership = formCategory === TicketingFormCategory.MembershipV2;
  const isShopForm = formCategory === TicketingFormCategory.Shop;
  const isSmsEnabled = Boolean(organization?.featureSmsNotificationEnabled);

  const supportsFulfillmentEmails = supportsFulfillment(formCategory as TicketingFormCategory);
  const offeredFulfillmentMethods = apiTicketing.offeredFulfillmentMethods ?? [];
  const hasPickupEmail = supportsFulfillmentEmails && offersPickup(offeredFulfillmentMethods);
  const hasShippingEmail = supportsFulfillmentEmails && offersShipping(offeredFulfillmentMethods);
  const showFulfillmentEmails = hasPickupEmail || hasShippingEmail;

  const isWebBasedTickets = isWebBasedTicketsEligible({
    formCategory: formCategory as TicketingFormCategory,
    generateQrCode: apiTicketing.generateQrCode,
  });

  const showReminders = isEvent && remindersDataFromContext.showReminders;
  const hasDate = Boolean(apiTicketing.recurrences?.length);
  const hasLocation = Boolean(apiTicketing.address);
  const hasETicket = Boolean(apiTicketing.generateQrCode) && !isWebBasedTickets;

  const localizedField = apiTicketing.ticketingFields?.find((field) => field.locale === apiTicketing.locale);

  const rates = apiTicketing.rates ?? [];
  const renewableRates = rates.filter(
    (rate) =>
      rate.membershipValidityPeriod === MembershipValidityPeriod.OneYearAfterPayment ||
      rate.membershipValidityPeriod === MembershipValidityPeriod.AtDate
  );
  const hasExpiringRates = renewableRates.length > 0;
  const hasAutoRenewalRates = renewableRates.some((rate) => rate.allowAutomaticRenewal);

  const orgName = apiTicketing.organization?.name || "";
  const userName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(" ");
  const locale = (apiTicketing.locale as Locales) || Locales.EN;

  const initialOpenCardFromUrl = getInitialOpenCardFromUrl(searchParams);
  const [openRow, setOpenRow] = useState<RowKey | null>(
    initialOpenCardFromUrl ?? (showFulfillmentEmails ? "thank-you-email" : null)
  );

  const syncEmailInPreview = (nextOpenRow: RowKey | null) => {
    if (!liveFormData?.automatedCommunicationsData) {
      return;
    }
    if (liveFormData.automatedCommunicationsData.emailInPreview === nextOpenRow) {
      return;
    }
    setLiveFormData({
      ...liveFormData,
      automatedCommunicationsData: { ...liveFormData.automatedCommunicationsData, emailInPreview: nextOpenRow },
    });
  };

  const toggleOpen = (row: RowKey) => {
    setOpenRow((current) => {
      const next = current === row ? null : row;
      syncEmailInPreview(next);
      return next;
    });
  };

  const openRowOnError = (row: RowKey) => {
    setOpenRow(row);
    syncEmailInPreview(row);
  };

  const handleInvalidSubmit = (errors: FieldErrors<AutomatedCommunicationsFormData>) => {
    const firstErroredRow = FORM_KEY_ORDER.find((key) => errors[key]);
    if (firstErroredRow) {
      openRowOnError(FORM_KEY_TO_ROW[firstErroredRow]);
    }
  };

  const { data: remindersData, isError: isRemindersError } = trpc.getLiveFormRemindersData.useQuery(
    { ticketingId: apiTicketing.id },
    { enabled: isAuction || showReminders }
  );

  const defaultValues = useMemo(
    () =>
      buildDefaultValues({
        apiTicketing,
        localizedField,
        remindersData,
        emailContents,
        orgName,
        userName,
        locale,
        t,
      }),
    [apiTicketing, localizedField, remindersData, emailContents, orgName, userName, locale, t]
  );

  const form = useForm<AutomatedCommunicationsFormData>({
    values: defaultValues,
    resetOptions: { keepDirtyValues: true },
  });
  const [isRemindersLoaded, setIsRemindersLoaded] = useState(false);
  useEffect(() => {
    if (remindersData || isRemindersError) {
      setIsRemindersLoaded(true);
    }
  }, [remindersData, isRemindersError]);
  useWarnIfUnsavedChanges(form.formState.isDirty);
  const { dirtyFields } = form.formState;

  const smsOptIn = useSmsOptInConfirmation(apiTicketing);
  const shouldShowCustomMessagesDisabledBanner = useShouldShowCustomMessagesDisabledBanner();

  const { isAIEligible } = useApplyThankYouAIDefaults(form, "automated-communications", {
    subject: "thankYouWinner.subject",
    content: "thankYouWinner.content",
  });
  useApplyReminderAIDefaults({
    form,
    showReminders,
    hasDate,
    eventName: localizedField?.title ?? "",
    isAIEligible,
    reminder1Exists: Boolean(remindersData?.reminder1),
    reminder2Exists: Boolean(remindersData?.reminder2),
  });

  useUpdateTicketingDataInPreview<AutomatedCommunicationsFormData>({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.automatedCommunicationsData.thankYou = {
        subject: formData.thankYouWinner.subject ?? t("common", "thankYouForYourSupport"),
        body: formData.thankYouWinner.content ?? t("dashboard", "editor.eventThankYouEmail.placeholder"),
      };

      if (isEvent || isAuction) {
        liveDataToMutate.automatedCommunicationsData.reminder1 = {
          subject: formData.reminder1.subject || "",
          body: formData.reminder1.content || "",
        };
        liveDataToMutate.automatedCommunicationsData.reminder2 = {
          subject: formData.reminder2.subject || "",
          body: formData.reminder2.content || "",
        };
      }

      if (isAuction) {
        liveDataToMutate.automatedCommunicationsData.thankYouBidPlaced = {
          subject: formData.thankYouBidPlaced.subject || "",
          body: formData.thankYouBidPlaced.content || "",
        };
        liveDataToMutate.automatedCommunicationsData.thankYouNonWinner = {
          subject: formData.thankYouNonWinner.subject || "",
          body: formData.thankYouNonWinner.content || "",
        };
      }

      if (showRenewalEmails) {
        liveDataToMutate.automatedCommunicationsData.expiringMembership = {
          subject: formData.expiringMembership.subject || "",
          body: formData.expiringMembership.content || "",
        };
        liveDataToMutate.automatedCommunicationsData.autoRenewal = {
          subject: formData.autoRenewal.subject || "",
          body: formData.autoRenewal.content || "",
        };
      }

      if (showFulfillmentEmails) {
        liveDataToMutate.automatedCommunicationsData.readyForPickup = {
          subject: formData.readyForPickup.subject || "",
          body: formData.readyForPickup.content || "",
        };
        liveDataToMutate.automatedCommunicationsData.orderShipped = {
          subject: formData.orderShipped.subject || "",
          body: formData.orderShipped.content || "",
        };
      }
    },
  });
  const hasSyncedInitialOpenCard = useRef(false);
  useEffect(() => {
    if (hasSyncedInitialOpenCard.current || !initialOpenCardFromUrl || !liveFormData?.automatedCommunicationsData) {
      return;
    }
    hasSyncedInitialOpenCard.current = true;
    syncEmailInPreview(initialOpenCardFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveFormData?.automatedCommunicationsData]);

  const { mutateAsync: updateAuctionLoseEmail } = trpc.updateAuctionLoseEmail.useMutation();
  const { mutateAsync: updateAuctionBidSubmittedEmail } = trpc.updateAuctionBidSubmittedEmail.useMutation();
  const { mutateAsync: updateTicketingAutomatedEmails } = trpc.updateTicketingAutomatedEmails.useMutation();
  const utils = trpc.useUtils();

  const buildReminderAttachmentPayload = (
    key: "reminder1" | "reminder2",
    formData: AutomatedCommunicationsFormData
  ): { attachmentUrl?: string | null } =>
    resolveReminderAttachmentPayload({
      dirty: Boolean(dirtyFields[key]?.attachment),
      displayedFileUrl: formData[key].attachment?.fileUrl,
      isStoredFileKnown: Boolean(remindersData?.[key]),
    });

  const isReminderEdited = (key: "reminder1" | "reminder2") => {
    const reminderDirtyFields = dirtyFields[key];
    return Boolean(
      reminderDirtyFields?.subject ||
      reminderDirtyFields?.content ||
      reminderDirtyFields?.attachment ||
      reminderDirtyFields?.offset ||
      reminderDirtyFields?.offsetUnit
    );
  };

  const resolveReminderContent = (key: "reminder1" | "reminder2", formData: AutomatedCommunicationsFormData) => ({
    subject: resolveReminderFieldValue({
      dirty: dirtyFields[key]?.subject,
      displayedValue: formData[key].subject,
      storedValue: remindersData?.[key]?.subject,
    }),
    content: resolveReminderFieldValue({
      dirty: dirtyFields[key]?.content,
      displayedValue: formData[key].content,
      storedValue: remindersData?.[key]?.body,
    }),
    ...buildReminderAttachmentPayload(key, formData),
  });

  const buildFulfillmentPayload = (formData: AutomatedCommunicationsFormData) => ({
    readyForPickup: {
      enabled: hasPickupEmail ? formData.readyForPickup.enabled : false,
      subject: formData.readyForPickup.subject || "",
      content: formData.readyForPickup.content || "",
    },
    orderShipped: {
      enabled: hasShippingEmail ? formData.orderShipped.enabled : false,
      subject: formData.orderShipped.subject || "",
      content: formData.orderShipped.content || "",
    },
  });

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<AutomatedCommunicationsFormData>({
    type: "ticketing",
    form,
    saveData: async (formData) => {
      if (isAuction) {
        const loseEmailResult = await updateAuctionLoseEmail({
          ticketingId: apiTicketing.id,
          subject: formData.thankYouNonWinner.subject || "",
          content: formData.thankYouNonWinner.content,
        });
        if (loseEmailResult.error) {
          return { data: null, error: loseEmailResult.error };
        }

        const bidPlacedResult = await updateAuctionBidSubmittedEmail({
          ticketingId: apiTicketing.id,
          subject: formData.thankYouBidPlaced.subject || "",
          content: formData.thankYouBidPlaced.content,
        });
        if (bidPlacedResult.error) {
          return { data: null, error: bidPlacedResult.error };
        }
        const result = await updateTicketingAutomatedEmails({
          ticketingId: apiTicketing.id,
          thankYou: {
            subject: formData.thankYouWinner.subject || "",
            content: formData.thankYouWinner.content,
            attachmentUrl: null,
          },
          reminders: {
            reminder1: {
              enabled: formData.reminder1.emailEnabled || formData.reminder1.smsEnabled,
              offset: formData.reminder1.offset,
              offsetUnit: formData.reminder1.offsetUnit,
              subject: formData.reminder1.subject || "",
              content: formData.reminder1.content || "",
              ...buildReminderAttachmentPayload("reminder1", formData),
              edited: isReminderEdited("reminder1"),
            },
            reminder2: {
              enabled: formData.reminder2.emailEnabled || formData.reminder2.smsEnabled,
              offset: formData.reminder2.offset,
              offsetUnit: formData.reminder2.offsetUnit,
              subject: formData.reminder2.subject || "",
              content: formData.reminder2.content || "",
              ...buildReminderAttachmentPayload("reminder2", formData),
              edited: isReminderEdited("reminder2"),
            },
          },
          auctionChannels: {
            outbidSmsEnabled: formData.outbid.smsEnabled,
            reminder1EmailEnabled: formData.reminder1.emailEnabled,
            reminder1SmsEnabled: formData.reminder1.smsEnabled,
            reminder2EmailEnabled: formData.reminder2.emailEnabled,
            reminder2SmsEnabled: formData.reminder2.smsEnabled,
          },
        });
        if (showFulfillmentEmails) {
          await updateTicketingAutomatedEmails({
            ticketingId: apiTicketing.id,
            thankYou: {
              subject: formData.thankYouWinner.subject || "",
              content: formData.thankYouWinner.content,
              attachmentUrl: null,
            },
            fulfillment: buildFulfillmentPayload(formData),
          });
        }
        await utils.form_getTicketingFrontendPage.invalidate();
        await utils.getLiveFormRemindersData.invalidate({ ticketingId: apiTicketing.id });
        await utils.getAutomatedEmailContents.invalidate({ ticketingId: apiTicketing.id });
        return result;
      }

      const result = await updateTicketingAutomatedEmails({
        ticketingId: apiTicketing.id,
        thankYou: {
          subject: formData.thankYouWinner.subject || "",
          content: formData.thankYouWinner.content,
          attachmentUrl: formData.thankYouWinner.attachment?.fileUrl ?? null,
        },
        reminders: showReminders
          ? {
              reminder1: {
                enabled: formData.reminder1.enabled,
                offset: formData.reminder1.offset,
                offsetUnit: formData.reminder1.offsetUnit,
                ...resolveReminderContent("reminder1", formData),
              },
              reminder2: {
                enabled: formData.reminder2.enabled,
                offset: formData.reminder2.offset,
                offsetUnit: formData.reminder2.offsetUnit,
                ...resolveReminderContent("reminder2", formData),
              },
            }
          : undefined,
        membership: showRenewalEmails
          ? {
              expiringMembership: {
                enabled: formData.expiringMembership.enabled ?? true,
                delayDays: formData.expiringMembership.delayDays ?? 30,
                subject: formData.expiringMembership.subject || "",
                content: formData.expiringMembership.content || "",
              },
              autoRenewal: {
                enabled: formData.autoRenewal.enabled ?? true,
                delayDays: formData.autoRenewal.delayDays ?? 30,
                subject: formData.autoRenewal.subject || "",
                content: formData.autoRenewal.content || "",
              },
            }
          : undefined,
        fulfillment: showFulfillmentEmails ? buildFulfillmentPayload(formData) : undefined,
      });
      await utils.getLiveFormRemindersData.invalidate({ ticketingId: apiTicketing.id });
      await utils.getAutomatedEmailContents.invalidate({ ticketingId: apiTicketing.id });
      return result;
    },
    errorMessage: t("dashboard", "ticketing.error.updateThankYouEmail"),
    onInvalid: handleInvalidSubmit,
  });

  useEffect(() => {
    const stepContent = document.querySelector('[data-test="step-content"]');
    if (!(stepContent instanceof HTMLElement)) {
      return undefined;
    }
    const previousScrollbarWidth = stepContent.style.scrollbarWidth;
    stepContent.style.scrollbarWidth = "none";
    const styleEl = document.createElement("style");
    styleEl.textContent = '[data-test="step-content"]::-webkit-scrollbar { display: none; }';
    document.head.appendChild(styleEl);
    return () => {
      stepContent.style.scrollbarWidth = previousScrollbarWidth;
      styleEl.remove();
    };
  }, []);

  const bodyCustomConfigEvent = {
    date: hasDate,
    location: hasLocation,
    eTicket: hasETicket,
    viewTickets: isWebBasedTickets,
  };

  const sharedRowProps = { openRow, onToggleOpen: toggleOpen, defaultValues };
  const sharedBodyProps = { hasDate, hasLocation };

  return (
    <>
      <LiveFormActionFooterPortal onBack={handleSaveAndBack} onNext={handleSaveAndNext} />
      <StepContainer
        title={t("dashboard", "ticketing.automatedCommunications.title")}
        description={t("dashboard", "ticketing.automatedCommunications.description")}
        isStepChanging={isStepChanging}
        slots={{ actionFooter: null }}
        data-test="live-form-editor-step-automated-communications"
      >
        <FormProvider {...form}>
          <Stack gap={1.5}>
            {shouldShowCustomMessagesDisabledBanner && <CustomMessagesDisabledBanner />}
            {isAuction && (
              <AuctionRows
                {...sharedRowProps}
                isSmsEnabled={isSmsEnabled}
                smsOptIn={smsOptIn}
                hasPickupEmail={hasPickupEmail}
                hasShippingEmail={hasShippingEmail}
              />
            )}
            {isEvent && (
              <EventRows
                {...sharedRowProps}
                {...sharedBodyProps}
                showReminders={showReminders}
                remindersData={remindersData}
                isMultiDate={isMultiDate}
                isRemindersLoaded={isRemindersLoaded}
                bodyCustomConfig={bodyCustomConfigEvent}
                onViewReminderResults={onViewReminderResults}
              />
            )}
            {isMembership && (
              <MembershipRows
                {...sharedRowProps}
                {...sharedBodyProps}
                showRenewalEmails={showRenewalEmails}
                hasExpiringRates={hasExpiringRates}
                hasAutoRenewalRates={hasAutoRenewalRates}
              />
            )}
            {isShopForm && (
              <ShopRows
                {...sharedRowProps}
                {...sharedBodyProps}
                hasPickupEmail={hasPickupEmail}
                hasShippingEmail={hasShippingEmail}
              />
            )}
            {!isAuction && !isEvent && !isMembership && !isShopForm && (
              <SimpleRows
                {...sharedRowProps}
                {...sharedBodyProps}
                isSweepstakes={formCategory === TicketingFormCategory.Sweepstakes}
              />
            )}
          </Stack>
        </FormProvider>
      </StepContainer>
      {isAuction && isSmsEnabled && (
        <SmsOptInConfirmModal open={smsOptIn.isOpen} onConfirm={smsOptIn.onConfirm} onCancel={smsOptIn.onCancel} />
      )}
    </>
  );
};
